import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../data-access/auth.service';

/**
 * A partner (ClearTax) can send a user to HashBooks to approve a connection. If they are signed out,
 * they log in first, and every login ends at the dashboard, which would drop the request. So the request
 * is remembered for the length of the login (this tab only, ten minutes at most, only this one path),
 * and the dashboard hands the user back to it.
 */
const KEY = 'hb:oauth-return';
const MAX_AGE_MS = 10 * 60_000;
/** Only the consent page with an opaque request id: nothing else can ever be "returned to". */
const ALLOWED = /^\/oauth\/authorize\?request=[A-Za-z0-9_-]{16,128}$/;

export function rememberOAuthReturn(path: string, now = Date.now()): void {
  if (!ALLOWED.test(path)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: now }));
  } catch {
    /* storage blocked: the user simply starts again from ClearTax */
  }
}

/** The remembered path, once; null when there is none, it is too old, or it is not the allowed shape. */
export function takeOAuthReturn(now = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof path !== 'string' || typeof at !== 'number' || now - at > MAX_AGE_MS || !ALLOWED.test(path)) return null;
    return path;
  } catch {
    return null;
  }
}

/** For the consent page: needs a signed-in, verified user, and remembers where to come back to. */
export const oauthAuthorizeGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) {
    rememberOAuthReturn(state.url);
    return router.createUrlTree(['/auth/login']);
  }
  if (auth.isEmailVerified()) return true;

  rememberOAuthReturn(state.url);
  inject(ToastrService).warning('Please verify your email to continue.', 'Email not verified');
  return router.createUrlTree(['/auth/verify'], { queryParams: { email: auth.getStoredUser()?.email ?? '' } });
};

/** On the dashboard: if a request was waiting for this login, go to it. Otherwise nothing changes. */
export const oauthReturnGuard: CanActivateFn = () => {
  const path = takeOAuthReturn();
  return path ? inject(Router).parseUrl(path) : true;
};
